// =============================================================================
// IMPORTS - Importation des dépendances nécessaires
// =============================================================================

// useEffect : Hook pour gérer les effets de bord (ex: réagir aux changements d'état)
// useState : Hook pour créer et gérer l'état local d'un composant
import { useEffect, useState } from 'react';

// Import des styles CSS pour ce composant
import './App.css';

// Import du composant Header qui affiche les scores et les dés
import Header from './components/Header';

// Import du composant Footer pour le pied de page
import Footer from './components/Footer';

// =============================================================================
// COMPOSANT PRINCIPAL - App
// =============================================================================
// Un composant React est une fonction qui retourne du JSX (HTML-like)
// C'est le composant racine de notre application

function App() {
  // ===========================================================================
  // ÉTAT (STATE) - Déclaration des variables d'état avec useState
  // ===========================================================================
  // useState retourne un tableau avec 2 éléments :
  // 1. La valeur actuelle de l'état
  // 2. Une fonction pour modifier cette valeur
  // Syntaxe : const [valeur, setValeur] = useState(valeurInitiale);

  // Score du joueur 1, initialisé à 0
  const [player1Score, setPlayer1Score] = useState(0);

  // Score du joueur 2, initialisé à 0
  const [player2Score, setPlayer2Score] = useState(0);

  // Joueur actuel (1 ou 2), commence par le joueur 1
  const [currentPlayer, setCurrentPlayer] = useState(1);

  // Message toast pour afficher le gagnant
  const [toast, setToast] = useState("");

  // Valeurs des deux dés, initialisées à 1
  const [dice1, setDice1] = useState(1);
  const [dice2, setDice2] = useState(1);

  // ===========================================================================
  // EFFET (useEffect) - Réagir aux changements d'état
  // ===========================================================================
  // useEffect permet d'exécuter du code quand certaines valeurs changent
  // Syntaxe : useEffect(callback, [dépendances])
  // - callback : fonction à exécuter
  // - dépendances : tableau des valeurs à surveiller (si vide [], exécuté une seule fois)

  useEffect(
    () => {
      // Ce code s'exécute à chaque fois que player1Score ou player2Score change
      // Vérifie si un joueur a gagné (score >= 25)
      if (player1Score >= 25) {
        setToast(`Bravo ! Le Joueur 1 gagne !`);
      } else if (player2Score >= 25) {
        setToast(`Bravo ! Le Joueur 2 gagne !`);
      }
    },
    [player1Score, player2Score] // Tableau de dépendances : surveille ces 2 variables
  );

  // ===========================================================================
  // FONCTION - rollDice (Lancer les dés)
  // ===========================================================================
  // Cette fonction est appelée quand l'utilisateur clique sur le bouton

  const rollDice = () => {
    // Ne pas permettre de jouer si quelqu'un a déjà gagné
    if (player1Score >= 25 || player2Score >= 25) return;

    // Math.random() génère un nombre entre 0 et 1 (exclus)
    // Math.floor() arrondit à l'entier inférieur
    // * 6 + 1 donne un nombre entre 1 et 6 (comme un vrai dé)
    const newDice1 = Math.floor(Math.random() * 6) + 1;
    const newDice2 = Math.floor(Math.random() * 6) + 1;

    // Met à jour l'état des dés avec les nouvelles valeurs
    setDice1(newDice1);
    setDice2(newDice2);

    // Calcule le score total des deux dés
    const calcScore = newDice1 + newDice2;

    // Ajoute le score au joueur actuel
    // On utilise la forme fonctionnelle (prev => prev + calcScore) pour éviter
    // les problèmes de synchronisation quand l'état dépend de sa valeur précédente
    if (currentPlayer === 1) {
      setPlayer1Score(prev => prev + calcScore);
    } else {
      setPlayer2Score(prev => prev + calcScore);
    }

    // Passe au joueur suivant avec un opérateur ternaire
    // condition ? valeurSiVrai : valeurSiFaux
    setCurrentPlayer(currentPlayer === 1 ? 2 : 1);
  }

  // ===========================================================================
  // RENDU JSX - Ce que le composant affiche à l'écran
  // ===========================================================================
  // JSX permet d'écrire du HTML dans JavaScript
  // Les accolades {} permettent d'insérer des expressions JavaScript

  return (
    <div className="App">
      {/*
        Composant Header avec des "props" (propriétés)
        Les props permettent de passer des données du parent vers l'enfant
        Syntaxe : nomProp={valeur}
      */}
      <Header
        player1={player1Score}
        player2={player2Score}
        dice1={dice1}
        dice2={dice2}
        currentPlayer={currentPlayer}
      />

      {/* Affiche le message toast (gagnant) avec style */}
      {toast && (
        <div className="toast">
          <i className="fas fa-trophy"></i> {toast}
        </div>
      )}

      {/*
        Bouton avec événement onClick
        onClick={rollDice} : appelle la fonction rollDice quand on clique
        Note : on passe la référence de la fonction, pas son appel rollDice()
      */}
      <button className="roll-button" onClick={rollDice}>
        <i className="fas fa-dice"></i> Lancer les Dés
      </button>

      {/* Section pédagogique - Concepts React utilisés */}
      <div className="description">
        <h2>
          <i className="fas fa-graduation-cap"></i> Concepts React utilisés
        </h2>
        <ul>
          <li>
            <i className="fas fa-check-circle"></i>
            <span><strong>useState</strong> : Gestion de l'état local (scores, dés, joueur actuel)</span>
          </li>
          <li>
            <i className="fas fa-check-circle"></i>
            <span><strong>useEffect</strong> : Effet de bord pour détecter le gagnant</span>
          </li>
          <li>
            <i className="fas fa-check-circle"></i>
            <span><strong>Props</strong> : Passage de données du composant parent vers l'enfant</span>
          </li>
          <li>
            <i className="fas fa-check-circle"></i>
            <span><strong>Composants</strong> : Séparation logique (App) / présentation (Header)</span>
          </li>
          <li>
            <i className="fas fa-check-circle"></i>
            <span><strong>Événements</strong> : Gestion du clic avec onClick</span>
          </li>
          <li>
            <i className="fas fa-check-circle"></i>
            <span><strong>Rendu conditionnel</strong> : Affichage dynamique selon l'état</span>
          </li>
        </ul>
      </div>

      {/* Composant Footer */}
      <Footer />
    </div>
  );
}

// =============================================================================
// EXPORT - Rend le composant disponible pour d'autres fichiers
// =============================================================================
// "export default" permet d'importer ce composant dans d'autres fichiers
// avec : import App from './App'

export default App;
